import React, { useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Accordion,
  AccordionItem,
  Spinner,
} from "@heroui/react";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  X,
  Download,
  Check,
  Warning,
  Database,
  Cloud,
  File,
  ArrowRight,
  Calculator,
  Folder,
} from "@phosphor-icons/react";
import { toast } from "react-toastify";

// Types matching backend DTO
interface UnitIdChangeRecord {
  id: string | number;
  field: string;
  oldValue: string;
  newValue: string;
}

interface UnitIdChangeEntityGroup {
  entity: string;
  tableName: string;
  recordCount: number;
  records: UnitIdChangeRecord[];
}

interface SharePointChange {
  type: "folder" | "file";
  action: "renamed" | "url_updated" | "simulated";
  oldPath: string;
  newPath: string;
}

export interface UnitIdChangeLog {
  timestamp: string;
  oldUnitId: string;
  newUnitId: string;
  buildingId: string;
  isProduction: boolean;
  changes: UnitIdChangeEntityGroup[];
  sharePointChanges: SharePointChange[];
  summary: {
    totalRecordsUpdated: number;
    totalSharePointChanges: number;
  };
  warnings: string[];
}

interface ChangeLogViewerProps {
  isOpen: boolean;
  onClose: () => void;
  changeLog: UnitIdChangeLog | null;
  onNavigateToNewUnit?: () => void;
}

// Generate text content for the change log (matches backend format)
const generateChangeLogText = (log: UnitIdChangeLog): string => {
  const lines: string[] = [];

  lines.push("======================================================");
  lines.push("UNIT ID CHANGE LOG");
  lines.push("======================================================");
  lines.push(`Timestamp: ${log.timestamp}`);
  lines.push(`Old Unit ID: ${log.oldUnitId}`);
  lines.push(`New Unit ID: ${log.newUnitId}`);
  lines.push(`Building: ${log.buildingId}`);
  lines.push(
    `Environment: ${log.isProduction ? "PRODUCTION" : "DEVELOPMENT (simulated SharePoint)"}`,
  );
  lines.push("");

  if (log.warnings.length > 0) {
    lines.push("------------------------------------------------------");
    lines.push("WARNINGS");
    lines.push("------------------------------------------------------");
    log.warnings.forEach((warning) => {
      lines.push(`  ⚠️ ${warning}`);
    });
    lines.push("");
  }

  lines.push("------------------------------------------------------");
  lines.push("DATABASE CHANGES");
  lines.push("------------------------------------------------------");
  lines.push("");

  for (const entityGroup of log.changes) {
    lines.push(
      `[${entityGroup.entity}] - ${entityGroup.recordCount} record(s) updated`,
    );
    for (const record of entityGroup.records) {
      lines.push(`  - Record #${record.id}: ${record.field} changed`);
      if (record.oldValue.length > 50 || record.newValue.length > 50) {
        lines.push(`    Old: ${record.oldValue}`);
        lines.push(`    New: ${record.newValue}`);
      } else {
        lines.push(`    "${record.oldValue}" -> "${record.newValue}"`);
      }
    }
    lines.push("");
  }

  // Separate Odoo and SharePoint changes
  const odooChanges = log.sharePointChanges.filter(c => c.oldPath.includes("Odoo"));
  const spChanges = log.sharePointChanges.filter(c => !c.oldPath.includes("Odoo"));

  lines.push("------------------------------------------------------");
  lines.push("SHAREPOINT CHANGES");
  lines.push("------------------------------------------------------");
  lines.push("");

  if (spChanges.length === 0) {
    lines.push("  No SharePoint changes");
    lines.push("");
  } else {
    for (const spChange of spChanges) {
      const actionLabel =
        spChange.action === "simulated"
          ? "SIMULATED"
          : spChange.action === "renamed"
            ? "RENAMED"
            : "URL UPDATED";
      lines.push(`[${spChange.type.toUpperCase()}] (${actionLabel})`);
      lines.push(`  Old: ${spChange.oldPath}`);
      lines.push(`  New: ${spChange.newPath}`);
      lines.push("");
    }
  }

  lines.push("------------------------------------------------------");
  lines.push("ODOO ANALYTIC ACCOUNT CHANGES");
  lines.push("------------------------------------------------------");
  lines.push("");

  if (odooChanges.length === 0) {
    lines.push("  No Odoo changes (unit had no linked analytic account)");
    lines.push("");
  } else {
    for (const odooChange of odooChanges) {
      const actionLabel =
        odooChange.action === "simulated"
          ? "SIMULATED"
          : odooChange.action === "renamed"
            ? "RENAMED"
            : "UPDATED";
      lines.push(`[ANALYTIC ACCOUNT] (${actionLabel})`);
      lines.push(`  Old: ${odooChange.oldPath.replace("Odoo Analytic Account: ", "")}`);
      lines.push(`  New: ${odooChange.newPath.replace("Odoo Analytic Account: ", "")}`);
      lines.push("");
    }
  }

  lines.push("------------------------------------------------------");
  lines.push("SUMMARY");
  lines.push("------------------------------------------------------");
  lines.push(`Total database records updated: ${log.summary.totalRecordsUpdated}`);
  lines.push(`Total SharePoint changes: ${spChanges.length}`);
  lines.push(`Total Odoo changes: ${odooChanges.length}`);
  lines.push(
    `SharePoint folder rename: ${log.isProduction ? "Yes (actual)" : "Simulated (dev)"}`,
  );
  lines.push(
    `Odoo analytic account rename: ${log.isProduction ? "Yes (actual)" : "Simulated (dev)"}`,
  );
  lines.push("");
  lines.push("======================================================");
  lines.push("END OF LOG");
  lines.push("======================================================");

  return lines.join("\n");
};

export const ChangeLogViewer: React.FC<ChangeLogViewerProps> = ({
  isOpen,
  onClose,
  changeLog,
  onNavigateToNewUnit,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (!changeLog) return null;

  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  const handleDownload = () => {
    setIsDownloading(true);
    try {
      // Generate text content locally (no server round-trip needed)
      const textContent = generateChangeLogText(changeLog);
      const blob = new Blob([textContent], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `unit-id-change-log-${changeLog.oldUnitId}-to-${changeLog.newUnitId}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Wijzigingslog gedownload");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Kon wijzigingslog niet downloaden");
    } finally {
      setIsDownloading(false);
    }
  };

  const getActionLabel = (action: SharePointChange["action"]) => {
    switch (action) {
      case "renamed":
        return "Hernoemd";
      case "url_updated":
        return "URL bijgewerkt";
      case "simulated":
        return "Gesimuleerd (dev)";
      default:
        return action;
    }
  };

  const getActionColor = (action: SharePointChange["action"]) => {
    switch (action) {
      case "renamed":
        return "text-moss-primary";
      case "url_updated":
        return "text-blue-600";
      case "simulated":
        return "text-status-warning";
      default:
        return "text-neutral-primary";
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        backdrop: "bg-black/50",
        base: "border border-neutral-primary/20 max-h-[90vh]",
        header: "border-b border-neutral-primary/20",
        footer: "border-t border-neutral-primary/20",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Check size={24} className="text-moss-primary" />
            <h3 className="text-neutral-primary text-xl font-semibold">
              Unit ID succesvol gewijzigd
            </h3>
          </div>
          <p className="text-neutral-primary/70 text-sm font-normal">
            Bekijk hieronder alle wijzigingen die zijn doorgevoerd
          </p>
        </ModalHeader>

        <ModalBody className="py-4">
          <div className="space-y-4">
            {/* Summary Card */}
            <div className="bg-moss-primary/10 space-y-3 rounded-lg p-4">
              <div className="flex items-center justify-center gap-4">
                <div className="rounded-lg bg-white px-4 py-2 font-mono text-sm shadow-sm">
                  {changeLog.oldUnitId}
                </div>
                <ArrowRight size={20} className="text-moss-primary" />
                <div className="bg-moss-primary/20 text-moss-primary rounded-lg px-4 py-2 font-mono text-sm font-semibold shadow-sm">
                  {changeLog.newUnitId}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-neutral-primary/70">Tijdstip:</span>
                  <p className="font-medium">
                    {formatTimestamp(changeLog.timestamp)}
                  </p>
                </div>
                <div>
                  <span className="text-neutral-primary/70">Gebouw:</span>
                  <p className="font-medium">{changeLog.buildingId}</p>
                </div>
                <div>
                  <span className="text-neutral-primary/70">
                    Records bijgewerkt:
                  </span>
                  <p className="text-moss-primary font-medium">
                    {changeLog.summary.totalRecordsUpdated}
                  </p>
                </div>
                <div>
                  <span className="text-neutral-primary/70">
                    SharePoint wijzigingen:
                  </span>
                  <p className="text-moss-primary font-medium">
                    {changeLog.sharePointChanges.filter(c => !c.oldPath.includes("Odoo")).length}
                  </p>
                </div>
                <div>
                  <span className="text-neutral-primary/70">
                    Odoo analytische rekening:
                  </span>
                  <p className="text-purple-600 font-medium">
                    {changeLog.sharePointChanges.filter(c => c.oldPath.includes("Odoo")).length > 0
                      ? "Bijgewerkt"
                      : "Niet van toepassing"}
                  </p>
                </div>
              </div>

              {!changeLog.isProduction && (
                <div className="text-status-warning flex items-center gap-2 text-sm">
                  <Warning size={16} />
                  <span>
                    Development omgeving - SharePoint wijzigingen zijn
                    gesimuleerd
                  </span>
                </div>
              )}
            </div>

            {/* Warnings */}
            {changeLog.warnings.length > 0 && (
              <div className="bg-status-warning/10 border-status-warning/30 rounded-lg border p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Warning size={18} className="text-status-warning" />
                  <span className="text-status-warning font-semibold">
                    Waarschuwingen
                  </span>
                </div>
                <ul className="list-inside list-disc space-y-1 text-sm">
                  {changeLog.warnings.map((warning, idx) => (
                    <li key={idx} className="text-neutral-primary/80">
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Database Changes Accordion */}
            <Accordion variant="bordered" className="px-0">
              <AccordionItem
                key="database"
                aria-label="Database wijzigingen"
                title={
                  <div className="flex items-center gap-2">
                    <Database size={18} className="text-blue-600" />
                    <span>
                      Database wijzigingen ({changeLog.changes.length} tabellen)
                    </span>
                  </div>
                }
              >
                <div className="space-y-3">
                  {changeLog.changes.map((entityGroup, idx) => (
                    <div
                      key={idx}
                      className="border-neutral-primary/10 rounded-lg border p-3"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {entityGroup.entity}
                        </span>
                        <span className="bg-neutral-primary/10 rounded px-2 py-1 text-xs">
                          {entityGroup.recordCount} record(s)
                        </span>
                      </div>
                      <div className="max-h-32 space-y-1 overflow-y-auto font-mono text-xs">
                        {entityGroup.records
                          .slice(0, 10)
                          .map((record, ridx) => (
                            <div key={ridx} className="text-neutral-primary/70">
                              #{record.id}: {record.field}
                            </div>
                          ))}
                        {entityGroup.records.length > 10 && (
                          <div className="text-neutral-primary/50 italic">
                            ... en {entityGroup.records.length - 10} meer
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionItem>

              <AccordionItem
                key="sharepoint"
                aria-label="SharePoint wijzigingen"
                title={
                  <div className="flex items-center gap-2">
                    <Cloud size={18} className="text-blue-500" />
                    <span>
                      SharePoint wijzigingen (
                      {changeLog.sharePointChanges.filter(c => !c.oldPath.includes("Odoo")).length})
                    </span>
                  </div>
                }
              >
                <div className="space-y-2">
                  {changeLog.sharePointChanges
                    .filter(c => !c.oldPath.includes("Odoo"))
                    .map((change, idx) => (
                    <div
                      key={idx}
                      className="border-neutral-primary/10 rounded-lg border p-3"
                    >
                      <div className="mb-2 flex items-center gap-2">
                        {change.type === "folder" ? (
                          <Folder size={14} className="text-amber-600" />
                        ) : (
                          <File size={14} className="text-blue-500" />
                        )}
                        <span className="text-xs font-medium uppercase">
                          {change.type}
                        </span>
                        <span
                          className={`text-xs ${getActionColor(change.action)}`}
                        >
                          ({getActionLabel(change.action)})
                        </span>
                      </div>
                      <div className="space-y-1 font-mono text-xs">
                        <div
                          className="text-neutral-primary/60 truncate"
                          title={change.oldPath}
                        >
                          Oud: {change.oldPath}
                        </div>
                        <div
                          className="text-moss-primary truncate"
                          title={change.newPath}
                        >
                          Nieuw: {change.newPath}
                        </div>
                      </div>
                    </div>
                  ))}
                  {changeLog.sharePointChanges.filter(c => !c.oldPath.includes("Odoo")).length === 0 && (
                    <div className="text-neutral-primary/50 text-sm italic">
                      Geen SharePoint wijzigingen
                    </div>
                  )}
                </div>
              </AccordionItem>

              <AccordionItem
                key="odoo"
                aria-label="Odoo wijzigingen"
                title={
                  <div className="flex items-center gap-2">
                    <Calculator size={18} className="text-purple-500" />
                    <span>
                      Odoo Analytische Rekening (
                      {changeLog.sharePointChanges.filter(c => c.oldPath.includes("Odoo")).length})
                    </span>
                  </div>
                }
              >
                <div className="space-y-2">
                  {changeLog.sharePointChanges
                    .filter(c => c.oldPath.includes("Odoo"))
                    .map((change, idx) => (
                    <div
                      key={idx}
                      className="border-neutral-primary/10 rounded-lg border p-3"
                    >
                      <div className="mb-2 flex items-center gap-2">
                        <Calculator size={14} className="text-purple-500" />
                        <span className="text-xs font-medium">
                          Analytische Rekening
                        </span>
                        <span
                          className={`text-xs ${getActionColor(change.action)}`}
                        >
                          ({getActionLabel(change.action)})
                        </span>
                      </div>
                      <div className="space-y-1 font-mono text-xs">
                        <div
                          className="text-neutral-primary/60 truncate"
                          title={change.oldPath}
                        >
                          Oud: {change.oldPath.replace("Odoo Analytic Account: ", "")}
                        </div>
                        <div
                          className="text-moss-primary truncate"
                          title={change.newPath}
                        >
                          Nieuw: {change.newPath.replace("Odoo Analytic Account: ", "")}
                        </div>
                      </div>
                    </div>
                  ))}
                  {changeLog.sharePointChanges.filter(c => c.oldPath.includes("Odoo")).length === 0 && (
                    <div className="text-neutral-primary/50 text-sm italic">
                      Geen Odoo wijzigingen (unit had geen gekoppelde analytische rekening)
                    </div>
                  )}
                </div>
              </AccordionItem>
            </Accordion>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="flex w-full gap-3">
            <ButtonNew
              label={isDownloading ? "Downloaden..." : "Download logbestand"}
              variant="secondaryMoss"
              icon={
                isDownloading ? <Spinner size="sm" /> : <Download size={16} />
              }
              onClick={handleDownload}
              className="flex-1"
              isDisabled={isDownloading}
            />
            {onNavigateToNewUnit && (
              <ButtonNew
                label="Ga naar nieuwe unit"
                variant="primaryMoss"
                icon={<ArrowRight size={16} />}
                onClick={onNavigateToNewUnit}
                className="flex-1"
              />
            )}
            {!onNavigateToNewUnit && (
              <ButtonNew
                label="Sluiten"
                variant="primaryMoss"
                icon={<X size={16} />}
                onClick={onClose}
                className="flex-1"
              />
            )}
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeLogViewer;
